import React, { useState, useRef, useEffect } from "react";
import { Bar } from "react-chartjs-2";
import {
    Chart as ChartJS,
    BarElement,
    CategoryScale,
    LinearScale,
    Title,
    Tooltip,
    Legend,
} from "chart.js";

ChartJS.register(
    BarElement,
    CategoryScale,
    LinearScale,
    Title,
    Tooltip,
    Legend
);

// External HTML tooltip handler to enable scrolling for large tooltips
const externalTooltipHandler = (context) => {
    const { chart, tooltip } = context;
    let tooltipEl = document.getElementById('chartjs-scrollable-tooltip');

    if (!tooltipEl) {
        tooltipEl = document.createElement('div');
        tooltipEl.id = 'chartjs-scrollable-tooltip';
        Object.assign(tooltipEl.style, {
            position: 'fixed',
            background: 'rgba(0, 0, 0, 0.88)',
            color: '#fff',
            borderRadius: '6px',
            padding: '10px 12px',
            fontSize: '12px',
            lineHeight: '1.4',
            maxHeight: '260px',
            maxWidth: '340px',
            overflowY: 'auto',
            overflowX: 'hidden',
            pointerEvents: 'auto',
            zIndex: '99999',
            transition: 'opacity 0.12s ease',
            boxShadow: '0 4px 14px rgba(0,0,0,0.3)',
            opacity: '0',
        });
        tooltipEl.style.setProperty('scrollbar-width', 'thin', 'important');
        document.body.appendChild(tooltipEl);

        tooltipEl.addEventListener('mouseenter', () => {
            tooltipEl._hovered = true;
            tooltipEl.style.opacity = '1';
            tooltipEl.style.pointerEvents = 'auto';
        });
        tooltipEl.addEventListener('mouseleave', () => {
            tooltipEl._hovered = false;
            tooltipEl.style.opacity = '0';
            tooltipEl.style.pointerEvents = 'none';
        });

        if (!document.getElementById('chartjs-scrollable-tooltip-style')) {
            const styleEl = document.createElement('style');
            styleEl.id = 'chartjs-scrollable-tooltip-style';
            styleEl.innerHTML = `
                #chartjs-scrollable-tooltip::-webkit-scrollbar { width: 8px !important; display: block !important; }
                #chartjs-scrollable-tooltip::-webkit-scrollbar-track { background: rgba(255,255,255,0.08) !important; border-radius: 4px !important; display: block !important; }
                #chartjs-scrollable-tooltip::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.45) !important; border-radius: 4px !important; display: block !important; }
                #chartjs-scrollable-tooltip::-webkit-scrollbar-thumb:hover { background: rgba(255,255,255,0.7) !important; }
            `;
            document.head.appendChild(styleEl);
        }
    }

    if (tooltip.opacity === 0) {
        if (tooltipEl._hovered) return;
        tooltipEl.style.opacity = '0';
        tooltipEl.style.pointerEvents = 'none';
        return;
    }

    const titleLines = tooltip.title || [];
    const bodyLines = tooltip.body.map((b) => b.lines);
    const footerLines = tooltip.footer || [];

    let innerHtml = '';
    if (titleLines.length) {
        innerHtml += `<div style="font-weight:bold;margin-bottom:6px;font-size:13px;">${titleLines.join('<br/>')}</div>`;
    }
    const rows = bodyLines
        .map((body, i) => {
            const text = Array.isArray(body)
                ? body.filter((l) => l && l.trim() !== "").join(" ")
                : (body || "").toString().trim();
            if (!text) return null;

            const valueMatch = text.match(/:\s*\$?(-?[0-9,]+(?:\.[0-9]+)?)/);
            const numericValue = valueMatch
                ? Number(valueMatch[1].replace(/,/g, ""))
                : Number.NEGATIVE_INFINITY;

            return {
                text,
                colors: tooltip.labelColors[i] || {},
                numericValue,
            };
        })
        .filter(Boolean)
        .sort((a, b) => b.numericValue - a.numericValue);

    rows.forEach((row) => {
        const swatch = `<span style="display:inline-block;width:10px;height:10px;border-radius:2px;background:${row.colors.backgroundColor};border:1px solid ${row.colors.borderColor || "rgba(255,255,255,0.3)"};margin-right:8px;flex-shrink:0;"></span>`;
        innerHtml += `<div style="display:flex;align-items:center;padding:2px 0;white-space:nowrap;">${swatch}<span>${row.text}</span></div>`;
    });
    if (footerLines.length) {
        innerHtml += `<div style="border-top:1px solid rgba(255,255,255,0.25);margin-top:6px;padding-top:6px;font-weight:bold;">${footerLines.join('<br/>')}</div>`;
    }
    tooltipEl.innerHTML = innerHtml;

    const canvasRect = chart.canvas.getBoundingClientRect();
    const tooltipWidth = tooltipEl.offsetWidth;
    const tooltipHeight = tooltipEl.offsetHeight;

    let left = canvasRect.left + tooltip.caretX + 14;
    let top = canvasRect.top + tooltip.caretY - tooltipHeight / 2;

    if (left + tooltipWidth > window.innerWidth - 8) {
        left = canvasRect.left + tooltip.caretX - tooltipWidth - 14;
    }
    if (left < 8) left = 8;
    if (top < 8) top = 8;
    if (top + tooltipHeight > window.innerHeight - 8) {
        top = window.innerHeight - tooltipHeight - 8;
    }

    tooltipEl.style.left = left + 'px';
    tooltipEl.style.top = top + 'px';
    tooltipEl.style.opacity = '1';
    tooltipEl.style.pointerEvents = 'auto';
};

const StackedBarChart = (props) => {
    const [showLegendModal, setShowLegendModal] = useState(false);
    const [legendDatasets, setLegendDatasets] = useState([]);
    const [hiddenDatasets, setHiddenDatasets] = useState(new Set());
    const [legendSearchTerm, setLegendSearchTerm] = useState('');
    const [showAll, setShowAll] = useState(false);
    const chartRef = useRef(null);

    // Initialize legend datasets when data changes
    useEffect(() => {
        const datasets = props?.data?.datasets?.filter(d => d.label !== "Show More..." && d.label !== "Show Less...") || [];
        const legendItems = datasets.map((dataset, index) => ({
            index,
            label: dataset.label,
            color: dataset.backgroundColor,
            visible: true,
        }));
        setLegendDatasets(legendItems);
        setHiddenDatasets(new Set());
    }, [props?.data?.datasets]);

    // Store chart instance
    useEffect(() => {
        if (chartRef.current) {
            window.chartInstanceDrilldown = { chartInstance: chartRef.current };
        }
    }, [chartRef]);

    const selectedmonthyear = props?.data?.clickedLabel?.split(" ");

    const selectedmonth = new Date(
        `${selectedmonthyear?.[0]} 1, ${selectedmonthyear?.[1]}`
    ).getMonth();

    const selectedyear = parseInt(selectedmonthyear?.[1], 10);

    const labels = (props?.data?.labels || []).map((d) => {
        const date = new Date(selectedyear, selectedmonth, d);

        const dayLetter = date
            .toLocaleDateString("en-US", {
                weekday: "short",
            })
            .charAt(0);

        return [d.toString(), dayLetter];
    });

    const importantCategories = (props?.data.datasets || []).slice(0, 4);

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: "bottom",
                labels: {
                    boxWidth: 12,
                    boxHeight: 12,
                    filter: (legendItem, chartData) => {
                        if (!showAll) {
                            if (
                                importantCategories.some((cat) => cat.label === legendItem.text)
                            ) {
                                return true;
                            }
                            return legendItem.text === "Show More...";
                        }
                        return true;
                    },
                },
                onClick: (e, legendItem, legend) => {
                    if (
                        legendItem.text === "Show More..." ||
                        legendItem.text === "Show Less..."
                    ) {
                        setShowLegendModal(true);
                        return;
                    }
                    ChartJS.defaults.plugins.legend.onClick.call(
                        this,
                        e,
                        legendItem,
                        legend
                    );
                },
            },
            tooltip: {
                mode: "index",
                intersect: false,
                enabled: false,
                external: externalTooltipHandler,

                callbacks: {
                    title: (tooltipItems) => {
                        return tooltipItems[0].label;
                    },

                    label: (tooltipItem) => {
                        const value = tooltipItem.raw;

                        if (!value || value <= 0) return null;

                        const runCount =
                            tooltipItem.dataset.run_count?.[tooltipItem.dataIndex] ?? 0;
                        if (runCount === 0) return `${tooltipItem.dataset.label}: $${Number(value).toFixed(4)}`;
                        return `${tooltipItem.dataset.label}(${runCount}): $${Number(value).toFixed(4)}`;
                    },

                    footer: (tooltipItems) => {
                        const total = tooltipItems
                            .map(item => item.raw || 0)
                            .reduce((sum, val) => sum + val, 0);

                        return total > 0 ? `Total: $${Number(total).toFixed(4)}` : null;
                    },
                },
            },
        },
        barThickness: props?.barthickness ? props?.barthickness : 40,
        scales: {
            x: {
                stacked: true,
                grid: {
                    display: false,
                },
                ticks: {
                    color: function (context) {
                        const day = Number(
                            context.chart.data.labels[context.index][0]
                        );

                        const date = new Date(
                            selectedyear,
                            selectedmonth,
                            day
                        );

                        const weekDay = date.getDay(); // 0 = Sunday, 6 = Saturday

                        return weekDay === 0 || weekDay === 6
                            ? "#ff0000"
                            : "#0e0d0d";
                    }
                }
            },
            y: {
                stacked: true,
                beginAtZero: true,
                ticks: {
                    callback: (value) => `$${value}`,
                },
                grid: {
                    display: true,
                    color: "#F1F1F1",
                },
            },
        },
    };

    const updatedDatasets = [...props?.data.datasets];

    if (updatedDatasets?.length > 4) {
        if (!showAll) {
            updatedDatasets.push({
                label: "Show More...",
                data: Array(labels.length).fill(0),
                backgroundColor: "#BBBBBB",
            });
        } else {
            updatedDatasets.push({
                label: "Show Less...",
                data: Array(labels.length).fill(0),
                backgroundColor: "#999999",
            });
        }
    }

    const data = { labels, datasets: updatedDatasets };
    return (
        <>
            <Bar ref={chartRef} data={data} options={options} />

            {/* Legend Modal */}
            {showLegendModal && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 9999,
                    }}
                    onClick={() => {
                        setShowLegendModal(false);
                        setLegendSearchTerm('');
                    }}
                >
                    <div
                        style={{
                            backgroundColor: '#fff',
                            borderRadius: '8px',
                            padding: '24px',
                            width: '500px',
                            height: '600px',
                            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                            position: 'relative',
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexShrink: 0 }}>
                            <h4 style={{ margin: 0, fontSize: '18px', fontWeight: 'bold' }}>Legend</h4>
                            <button
                                onClick={() => {
                                    setShowLegendModal(false);
                                    setLegendSearchTerm('');
                                }}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    fontSize: '24px',
                                    cursor: 'pointer',
                                    color: '#999',
                                }}
                            >
                                ✕
                            </button>
                        </div>

                        {/* Search Input */}
                        <input
                            type="text"
                            placeholder="Search items..."
                            value={legendSearchTerm}
                            onChange={(e) => setLegendSearchTerm(e.target.value)}
                            style={{
                                width: '100%',
                                padding: '8px 12px',
                                marginBottom: '12px',
                                border: '1px solid #ddd',
                                borderRadius: '4px',
                                fontSize: '14px',
                                boxSizing: 'border-box',
                                flexShrink: 0,
                            }}
                        />

                        {/* Select All / Unselect All Buttons */}
                        <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', flexShrink: 0 }}>
                            <button
                                onClick={() => {
                                    if (window.chartInstanceDrilldown && window.chartInstanceDrilldown.chartInstance) {
                                        const chart = window.chartInstanceDrilldown.chartInstance;
                                        try {
                                            legendDatasets.forEach((dataset) => {
                                                const meta = chart.getDatasetMeta(dataset.index);
                                                meta.hidden = false;
                                            });
                                            chart.update();
                                            setLegendDatasets(
                                                legendDatasets.map((d) => ({ ...d, visible: true }))
                                            );
                                        } catch (err) {
                                            console.error('Error selecting all:', err);
                                        }
                                    }
                                }}
                                style={{
                                    flex: 1,
                                    padding: '6px 12px',
                                    border: '1px solid #007bff',
                                    borderRadius: '4px',
                                    backgroundColor: '#007bff',
                                    color: '#fff',
                                    cursor: 'pointer',
                                    fontSize: '12px',
                                    fontWeight: '500',
                                }}
                            >
                                Select All
                            </button>
                            <button
                                onClick={() => {
                                    if (window.chartInstanceDrilldown && window.chartInstanceDrilldown.chartInstance) {
                                        const chart = window.chartInstanceDrilldown.chartInstance;
                                        try {
                                            legendDatasets.forEach((dataset) => {
                                                const meta = chart.getDatasetMeta(dataset.index);
                                                meta.hidden = true;
                                            });
                                            chart.update();
                                            setLegendDatasets(
                                                legendDatasets.map((d) => ({ ...d, visible: false }))
                                            );
                                        } catch (err) {
                                            console.error('Error unselecting all:', err);
                                        }
                                    }
                                }}
                                style={{
                                    flex: 1,
                                    padding: '6px 12px',
                                    border: '1px solid #dc3545',
                                    borderRadius: '4px',
                                    backgroundColor: '#dc3545',
                                    color: '#fff',
                                    cursor: 'pointer',
                                    fontSize: '12px',
                                    fontWeight: '500',
                                }}
                            >
                                Unselect All
                            </button>
                        </div>

                        {/* Legend Items */}
                        <div style={{ borderTop: '1px solid #eee', paddingTop: '12px', flex: 1, overflowY: 'auto', minHeight: 0 }}>
                            {legendDatasets
                                .filter((dataset) =>
                                    dataset.label.toLowerCase().includes(legendSearchTerm.toLowerCase())
                                )
                                .map((dataset, index) => (
                                    <div
                                        key={index}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            padding: '12px 12px',
                                            borderRadius: '4px',
                                            marginBottom: '8px',
                                            backgroundColor: dataset.visible ? '#d4edda' : '#e2e3e5',
                                            cursor: 'pointer',
                                            transition: 'background-color 0.2s',
                                            border: '1px solid ' + (dataset.visible ? '#28a745' : '#d0d0d0'),
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.backgroundColor = dataset.visible ? '#c3e6cb' : '#d3d4d7';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.backgroundColor = dataset.visible ? '#d4edda' : '#e2e3e5';
                                        }}
                                        onClick={() => {
                                            if (window.chartInstanceDrilldown && window.chartInstanceDrilldown.chartInstance) {
                                                const chart = window.chartInstanceDrilldown.chartInstance;
                                                try {
                                                    const meta = chart.getDatasetMeta(dataset.index);
                                                    const currentHidden = meta.hidden;
                                                    meta.hidden = !currentHidden;
                                                    chart.update();

                                                    setLegendDatasets((prevDatasets) =>
                                                        prevDatasets.map((d) =>
                                                            d.index === dataset.index
                                                                ? { ...d, visible: currentHidden }
                                                                : d
                                                        )
                                                    );
                                                } catch (err) {
                                                    console.error('Error toggling visibility:', err);
                                                }
                                            }
                                        }}
                                    >
                                        <span
                                            style={{
                                                display: 'inline-block',
                                                width: '12px',
                                                height: '12px',
                                                backgroundColor: dataset.color,
                                                borderRadius: '2px',
                                                marginRight: '10px',
                                            }}
                                        />
                                        <span style={{ flex: 1, fontSize: '14px' }}>{dataset.label}</span>
                                    </div>
                                ))}
                            {legendDatasets.filter((dataset) =>
                                dataset.label.toLowerCase().includes(legendSearchTerm.toLowerCase())
                            ).length === 0 && (
                                    <div style={{ textAlign: 'center', padding: '16px', color: '#999' }}>
                                        No legend items found
                                    </div>
                                )}
                        </div>

                        {/* Close Button */}
                        <div style={{ marginTop: 'auto', display: 'flex', gap: '8px', justifyContent: 'flex-end', borderTop: '1px solid #eee', paddingTop: '12px', flexShrink: 0 }}>
                            <button
                                onClick={() => {
                                    setShowLegendModal(false);
                                    setLegendSearchTerm('');
                                }}
                                style={{
                                    padding: '8px 16px',
                                    border: '1px solid #ddd',
                                    borderRadius: '4px',
                                    backgroundColor: '#fff',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    fontWeight: '500',
                                }}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default StackedBarChart;
